import React, { useMemo } from 'react';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';

const AIVisibilityScore = ({ score = 0, trend = [], loading = false }) => {
  const circumference = 2 * Math.PI * 40;
  const scorePercent = Math.min(Math.max(score, 0), 100);
  const offset = circumference - (scorePercent / 100) * circumference;

  const { label, color, ringColor, bgColor } = useMemo(() => {
    if (score >= 60) return { label: 'GOOD', color: 'text-emerald-500', ringColor: '#10B981', bgColor: 'bg-emerald-50' };
    if (score >= 40) return { label: 'FAIR', color: 'text-amber-500', ringColor: '#F59E0B', bgColor: 'bg-amber-50' };
    return { label: 'POOR', color: 'text-red-500', ringColor: '#EF4444', bgColor: 'bg-red-50' };
  }, [score]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 animate-pulse" role="region" aria-label="AI Visibility Score loading">
        <div className="h-4 bg-gray-200 rounded w-32 mb-4"></div>
        <div className="flex items-center gap-4">
          <div className="w-28 h-28 bg-gray-200 rounded-full"></div>
          <div className="flex-1 h-16 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" role="region" aria-label={`AI Visibility Score: ${score} out of 100, rated ${label}`}>
      <h2 className="text-sm font-medium text-gray-500 mb-3">AI Visibility Score</h2>
      <div className="flex items-center gap-4">
        {/* Circular Gauge */}
        <div className="relative w-28 h-28 flex-shrink-0">
          <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 100 100" role="img" aria-label={`Score gauge showing ${score} percent`}>
            <circle cx="50" cy="50" r="40" stroke="#E5E7EB" strokeWidth="8" fill="none" />
            <circle
              cx="50" cy="50" r="40"
              stroke={ringColor}
              strokeWidth="8"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-bold text-gray-900" aria-hidden="true">{score}</span>
            <span className={`text-xs font-semibold ${color}`} aria-hidden="true">{label}</span>
          </div>
        </div>

        {/* Mini Trend Sparkline */}
        {trend.length > 0 && (
          <div className="flex-1 h-16" aria-label="Visibility score trend over time">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <Tooltip
                  contentStyle={{ fontSize: '12px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #E5E7EB' }}
                  labelStyle={{ display: 'none' }}
                  formatter={(value) => [`${value}`, 'Score']}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke={ringColor}
                  strokeWidth={2}
                  dot={{ r: 3, fill: ringColor, strokeWidth: 0 }}
                  activeDot={{ r: 5, fill: ringColor, strokeWidth: 2, stroke: '#fff' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIVisibilityScore;
